// Typography: one <i80-text> that renders the tag you ask for, with the i80 type styles.
//
// Helios splits this into Text::Display, Text::Body and Text::Code, but all three render
// the same element with the same classes; the only difference is which sizes and weights
// each style allows. In HTML that is one tag with a `variant` attribute.
import { I80Element, define, h } from "../base.js";

const VARIANTS = ["display", "body", "code"];

// Tags a line of text can reasonably be. Ember accepts any tag name; here the list is
// checked so a typo is reported instead of silently creating an unknown element.
const TAGS = ["span", "div", "p", "h1", "h2", "h3", "h4", "h5", "h6", "code", "pre", "strong",
  "em", "small", "label", "legend", "li", "dt", "dd", "figcaption", "blockquote", "caption",
  "summary", "address", "cite", "abbr", "time", "kbd", "samp", "td", "th"];

const ALIGNS = ["left", "center", "right"];

const COLORS = ["primary", "strong", "faint", "disabled", "high-contrast", "action", "action-hover",
  "action-active", "highlight", "highlight-on-surface", "highlight-high-contrast", "success",
  "success-on-surface", "success-high-contrast", "warning", "warning-on-surface",
  "warning-high-contrast", "critical", "critical-on-surface", "critical-high-contrast"];

// Only some size + weight combinations exist in the type scale (see Foundations/Typography).
const SIZES = { display: ["100", "200", "300", "400", "500"], body: ["100", "200", "300"], code: ["100", "200", "300"] };
const WEIGHTS = {
  display: { 100: ["medium"], 200: ["semibold"], 300: ["medium", "semibold", "bold"], 400: ["medium", "semibold", "bold"], 500: ["bold"] },
  body: { 100: ["regular", "medium", "semibold"], 200: ["regular", "medium", "semibold"], 300: ["regular", "medium", "semibold"] },
  code: { 100: ["regular", "bold"], 200: ["regular", "bold"], 300: ["regular", "bold"] },
};
// Display text has a different default weight per size; body and code are regular throughout.
const DEFAULT_WEIGHTS = {
  display: { 100: "medium", 200: "semibold", 300: "semibold", 400: "semibold", 500: "bold" },
  body: { 100: "regular", 200: "regular", 300: "regular" },
  code: { 100: "regular", 200: "regular", 300: "regular" },
};

/**
 * <i80-text tag="h1" variant="display" size="500">Page title</i80-text>
 * <i80-text tag="p">Paragraph text</i80-text>
 * <i80-text tag="pre" variant="code" weight="bold">$ i80 deploy</i80-text>
 *
 * For static text a class does the same job with no element:
 * <h1 class="i80-text i80-typography-display-500 i80-font-weight-bold">Page title</h1>
 */
export class I80Text extends I80Element {
  static get observedAttributes() {
    return ["variant", "size", "weight", "align", "color", "tag", "class"];
  }

  setup() {
    this._box = this.part("box");
    if (!this._box) {
      this._box = h(this.tag, { "data-i80-part": "box" }, ...this.takeContent());
      this.append(this._box);
    }
  }

  adopt() {
    this._box.append(...this.takeContent());
  }

  get tag() {
    return this.choice("tag", TAGS, "span");
  }

  get variant() {
    return this.choice("variant", VARIANTS, "body");
  }

  get size() {
    return this.choice("size", SIZES[this.variant], "200");
  }

  get weight() {
    const { variant, size } = this;
    return this.choice("weight", WEIGHTS[variant][size], DEFAULT_WEIGHTS[variant][size]);
  }

  update() {
    // The tag is an attribute, so it can change: build the new element and move the text over.
    if (this._box.localName !== this.tag) {
      const box = h(this.tag, { "data-i80-part": "box" }, ...this._box.childNodes);
      this._box.replaceWith(box);
      this._box = box;
    }
    const color = this.getAttribute("color");
    const predefined = COLORS.includes(color);
    this._box.className = [
      "i80-text",
      `i80-typography-${this.variant}-${this.size}`,
      `i80-font-weight-${this.weight}`,
      this.getAttribute("align") && `i80-text--align-${this.choice("align", ALIGNS, "left")}`,
      predefined && `i80-foreground-${color}`,
      // Ember forwards the author's class to the rendered tag (...attributes); so do we,
      // because the tag itself is display:contents and cannot be styled.
      this.getAttribute("class"),
    ].filter(Boolean).join(" ");
    // Anything that is not one of the predefined colours is a CSS colour, applied inline.
    this._box.style.color = color && !predefined ? color : "";
  }
}

define("i80-text", I80Text);
